// src/lib/recommendations.ts
// Smart learning recommendations based on user progress

import { Course, ActivityLog } from '../types';
import { supabase } from '../config/supabase';

export interface Recommendation {
  emoji: string;
  text: string;
  route: string;
  priority: number; // Higher = more urgent
  reason?: string;
}

export interface CourseProgress {
  lastActivity: ActivityLog | null;
  quizBestScore: number | null;
  quizLastScore: number | null;
  fillInBestScore: number | null;
  fillInLastScore: number | null;
  transformBestScore: number | null;
  transformLastScore: number | null;
  flashcardKnown: number;
  flashcardTotal: number;
  flashcardWeaknesses: number;
  vocabKnown: number;
  vocabTotal: number;
  lessonsCompleted: number;
  lessonsTotal: number;
  hasStarted: boolean;
}

// Fetch all progress data for a course
export async function getCourseProgress(
  userId: string,
  courseId: string,
  course: Course
): Promise<CourseProgress> {
  const progress: CourseProgress = {
    lastActivity: null,
    quizBestScore: null,
    quizLastScore: null,
    fillInBestScore: null,
    fillInLastScore: null,
    transformBestScore: null,
    transformLastScore: null,
    flashcardKnown: 0,
    flashcardTotal: course.flashcards?.length || 0,
    flashcardWeaknesses: 0,
    vocabKnown: 0,
    vocabTotal: 0,
    lessonsCompleted: 0,
    lessonsTotal: (course.textLessons?.length || 0) + (course.lessons?.length || 0),
    hasStarted: false,
  };

  try {
    // 1. Get last activity
    const { data: activities } = await supabase
      .from('activity_log')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(50);

    if (activities && activities.length > 0) {
      // Find last activity for this course
      const courseActivity = activities.find(a => {
        const details = a.details as Record<string, unknown>;
        return details?.course_id === courseId;
      });
      if (courseActivity) {
        progress.lastActivity = courseActivity;
        progress.hasStarted = true;
      }
    }

    // 2. Get quiz results
    const { data: quizResults } = await supabase
      .from('quiz_results')
      .select('percentage, mode, created_at')
      .eq('user_id', userId)
      .eq('course_id', courseId)
      .order('created_at', { ascending: false });

    if (quizResults && quizResults.length > 0) {
      progress.hasStarted = true;
      // Quiz mode
      const quizOnly = quizResults.filter(r => r.mode === 'quiz');
      if (quizOnly.length > 0) {
        progress.quizLastScore = quizOnly[0].percentage;
        progress.quizBestScore = Math.max(...quizOnly.map(r => r.percentage));
      }

      // Fill-in mode
      const fillInOnly = quizResults.filter(r => r.mode === 'fill-in');
      if (fillInOnly.length > 0) {
        progress.fillInLastScore = fillInOnly[0].percentage;
        progress.fillInBestScore = Math.max(...fillInOnly.map(r => r.percentage));
      }

      // Transform mode
      const transformOnly = quizResults.filter(r => r.mode === 'transform');
      if (transformOnly.length > 0) {
        progress.transformLastScore = transformOnly[0].percentage;
        progress.transformBestScore = Math.max(...transformOnly.map(r => r.percentage));
      }
    }

    // 3. Get activity_log for fill-in and transform if not in quiz_results
    if (progress.fillInLastScore === null) {
      const { data: fillInLogs } = await supabase
        .from('activity_log')
        .select('details')
        .eq('user_id', userId)
        .in('action', ['fill_in', 'fill_in_quiz'])
        .order('created_at', { ascending: false })
        .limit(5);

      if (fillInLogs && fillInLogs.length > 0) {
        progress.hasStarted = true;
        const scores = fillInLogs.map(l => {
          const d = l.details as { percentage?: number; score?: number; total?: number };
          if (d.percentage !== undefined) return d.percentage;
          if (d.score !== undefined && d.total) return Math.round((d.score / d.total) * 100);
          return null;
        }).filter((s): s is number => s !== null);

        if (scores.length > 0) {
          progress.fillInLastScore = scores[0];
          progress.fillInBestScore = Math.max(...scores);
        }
      }
    }

    if (progress.transformLastScore === null) {
      const { data: transformLogs } = await supabase
        .from('activity_log')
        .select('details')
        .eq('user_id', userId)
        .eq('action', 'transform_quiz')
        .order('created_at', { ascending: false })
        .limit(5);

      if (transformLogs && transformLogs.length > 0) {
        progress.hasStarted = true;
        const scores = transformLogs.map(l => {
          const d = l.details as { percentage?: number; score?: number; total?: number };
          if (d.percentage !== undefined) return d.percentage;
          if (d.score !== undefined && d.total) return Math.round((d.score / d.total) * 100);
          return null;
        }).filter((s): s is number => s !== null);

        if (scores.length > 0) {
          progress.transformLastScore = scores[0];
          progress.transformBestScore = Math.max(...scores);
        }
      }
    }

    // 4. Get flashcard progress
    const { data: flashcardProgress } = await supabase
      .from('flashcard_progress')
      .select('flashcard_id, knew')
      .eq('user_id', userId)
      .eq('course_id', courseId);

    if (flashcardProgress && flashcardProgress.length > 0) {
      progress.hasStarted = true;
      // Separate vocab (vocab_*) from regular flashcards
      const vocabProgress = flashcardProgress.filter(p => p.flashcard_id.startsWith('vocab_'));
      const fcProgress = flashcardProgress.filter(p => !p.flashcard_id.startsWith('vocab_'));

      progress.flashcardKnown = fcProgress.filter(p => p.knew).length;
      progress.flashcardWeaknesses = fcProgress.filter(p => !p.knew).length;
      progress.vocabKnown = vocabProgress.filter(p => p.knew).length;
      progress.vocabTotal = course.vocabSets?.reduce((sum, vs) => sum + vs.pairs.length, 0) || 0;
    }

    // 5. Get lesson progress
    const { data: lessonProgress } = await supabase
      .from('lesson_progress')
      .select('lesson_id, completed')
      .eq('user_id', userId)
      .eq('course_id', courseId);

    if (lessonProgress) {
      progress.lessonsCompleted = lessonProgress.filter(p => p.completed).length;
      if (progress.lessonsCompleted > 0) {
        progress.hasStarted = true;
      }
    }

  } catch (error) {
    console.error('getCourseProgress error:', error);
  }

  return progress;
}

// Get the next recommended action based on progress
export function getNextRecommendation(
  courseId: string,
  progress: CourseProgress,
  course: Course,
  context?: {
    justCompleted?: 'quiz' | 'fill-in' | 'transform' | 'flashcards' | 'vocab-match' | 'lesson';
    lastScore?: number;
    wrongCategories?: string[];
    category?: string; // Current category filter (e.g. 'kugel')
  }
): Recommendation {
  const recommendations: Recommendation[] = [];
  const hasQuiz = (course.quizQuestions?.length || 0) > 0;
  const hasFillIn = (course.fillInQuestions?.length || 0) > 0;
  const hasTransform = (course.transformQuestions?.length || 0) > 0;
  const hasFlashcards = (course.flashcards?.length || 0) > 0;
  const hasVocab = (course.vocabSets?.length || 0) > 0;
  const hasLessons = (course.textLessons?.length || 0) > 0 || (course.lessons?.length || 0) > 0;

  // Context-aware recommendations (after just completing something)
  if (context?.justCompleted) {
    const score = context.lastScore || 0;

    // After Quiz with low score
    if (context.justCompleted === 'quiz' && score < 70) {
      if (hasLessons) {
        recommendations.push({
          emoji: '📖',
          text: 'Lies nochmal die Lektionen durch, dann versuch es erneut.',
          route: `/course/${courseId}/lessons`,
          priority: 100,
          reason: 'Quiz war unter 70%',
        });
      }
      if (hasFlashcards) {
        recommendations.push({
          emoji: '📚',
          text: 'Uebe die Lernkarten, um das Wissen zu festigen.',
          route: `/course/${courseId}/flashcards`,
          priority: 95,
        });
      }
    }

    // After Quiz with good score - suggest next module
    if (context.justCompleted === 'quiz' && score >= 70) {
      if (hasFillIn && progress.fillInBestScore === null) {
        recommendations.push({
          emoji: '✍️',
          text: 'Super Quiz! Jetzt den Lückentext probieren.',
          route: `/course/${courseId}/fill-in`,
          priority: 100,
        });
      } else if (hasTransform && progress.transformBestScore === null) {
        recommendations.push({
          emoji: '🔄',
          text: 'Weiter mit Indirect Speech umformen!',
          route: `/course/${courseId}/transform`,
          priority: 100,
        });
      }
    }

    // After Fill-In with low score
    if (context.justCompleted === 'fill-in' && score < 70) {
      if (hasLessons) {
        recommendations.push({
          emoji: '📖',
          text: 'Schau dir die Grammatik-Regeln nochmal an.',
          route: `/course/${courseId}/lessons`,
          priority: 100,
        });
      }
    }

    // After Fill-In with good score
    if (context.justCompleted === 'fill-in' && score >= 70) {
      if (hasTransform && progress.transformBestScore === null) {
        recommendations.push({
          emoji: '🔄',
          text: 'Lückentext geschafft! Jetzt Indirect Speech.',
          route: `/course/${courseId}/transform`,
          priority: 100,
        });
      } else if (hasQuiz && (progress.quizBestScore || 0) < 90) {
        recommendations.push({
          emoji: '📝',
          text: 'Versuch das Quiz nochmal für eine bessere Note!',
          route: `/course/${courseId}/quiz`,
          priority: 95,
        });
      }
    }

    // After Flashcards with many wrong
    if (context.justCompleted === 'flashcards' && score < 70) {
      const catParam = context.category ? `&category=${context.category}` : '';
      recommendations.push({
        emoji: '🔄',
        text: 'Mach den Schwächen-Modus für die falschen Karten.',
        route: `/course/${courseId}/flashcards?mode=weakness${catParam}`,
        priority: 100,
        reason: 'Viele Karten nicht gewusst',
      });
    }

    // After good flashcards session
    if (context.justCompleted === 'flashcards' && score >= 80) {
      if (hasVocab && progress.vocabKnown < progress.vocabTotal) {
        recommendations.push({
          emoji: '🎮',
          text: 'Vokabeln sitzen! Teste dich im Zuordnungs-Spiel.',
          route: `/course/${courseId}/vocab-match`,
          priority: 100,
        });
      } else if (hasQuiz) {
        recommendations.push({
          emoji: '📝',
          text: 'Bereit fürs Quiz? Teste dein Wissen!',
          route: `/course/${courseId}/quiz`,
          priority: 95,
        });
      }
    }
  }

  // Default priority-based recommendations (when not context-specific)

  // Priority 1: Never started - start with lessons or flashcards
  if (!progress.hasStarted) {
    if (hasLessons) {
      recommendations.push({
        emoji: '📖',
        text: 'Starte mit den Lektionen!',
        route: `/course/${courseId}/lessons`,
        priority: 90,
      });
    } else if (hasFlashcards) {
      recommendations.push({
        emoji: '📚',
        text: 'Starte mit den Lernkarten!',
        route: `/course/${courseId}/flashcards`,
        priority: 90,
      });
    }
  }

  // Priority 2: Flashcard weaknesses to fix
  if (progress.flashcardWeaknesses > 3) {
    recommendations.push({
      emoji: '🔄',
      text: `${progress.flashcardWeaknesses} Lernkarten brauchen Wiederholung`,
      route: `/course/${courseId}/flashcards?mode=weakness`,
      priority: 85,
    });
  }

  // Priority 3: Unfinished modules (never done)
  if (hasQuiz && progress.quizBestScore === null) {
    recommendations.push({
      emoji: '📝',
      text: 'Quiz noch nicht gemacht - probier es aus!',
      route: `/course/${courseId}/quiz`,
      priority: 80,
    });
  }

  if (hasFillIn && progress.fillInBestScore === null) {
    recommendations.push({
      emoji: '✍️',
      text: 'Lückentext noch nicht gemacht',
      route: `/course/${courseId}/fill-in`,
      priority: 78,
    });
  }

  if (hasTransform && progress.transformBestScore === null) {
    recommendations.push({
      emoji: '🔄',
      text: 'Indirect Speech noch nicht geuebt',
      route: `/course/${courseId}/transform`,
      priority: 76,
    });
  }

  if (hasFlashcards && progress.flashcardKnown === 0) {
    recommendations.push({
      emoji: '📚',
      text: 'Lernkarten noch nicht gestartet',
      route: `/course/${courseId}/flashcards`,
      priority: 75,
    });
  }

  if (hasVocab && progress.vocabKnown === 0) {
    recommendations.push({
      emoji: '🎮',
      text: 'Vokabel-Spiel noch nicht gemacht',
      route: `/course/${courseId}/vocab-match`,
      priority: 74,
    });
  }

  // Priority 4: Modules with poor scores (<70%)
  if (progress.quizBestScore !== null && progress.quizBestScore < 70) {
    recommendations.push({
      emoji: '📝',
      text: `Quiz verbessern (aktuell ${progress.quizBestScore}%)`,
      route: `/course/${courseId}/quiz`,
      priority: 70,
    });
  }

  if (progress.fillInBestScore !== null && progress.fillInBestScore < 70) {
    recommendations.push({
      emoji: '✍️',
      text: `Lückentext verbessern (aktuell ${progress.fillInBestScore}%)`,
      route: `/course/${courseId}/fill-in`,
      priority: 68,
    });
  }

  if (progress.transformBestScore !== null && progress.transformBestScore < 70) {
    recommendations.push({
      emoji: '🔄',
      text: `Indirect Speech verbessern (aktuell ${progress.transformBestScore}%)`,
      route: `/course/${courseId}/transform`,
      priority: 66,
    });
  }

  // Priority 5: Good but not perfect (<90%)
  if (progress.quizBestScore !== null && progress.quizBestScore >= 70 && progress.quizBestScore < 90) {
    recommendations.push({
      emoji: '⭐',
      text: `Quiz auf 90%+ bringen (aktuell ${progress.quizBestScore}%)`,
      route: `/course/${courseId}/quiz`,
      priority: 50,
    });
  }

  if (progress.fillInBestScore !== null && progress.fillInBestScore >= 70 && progress.fillInBestScore < 90) {
    recommendations.push({
      emoji: '⭐',
      text: `Lückentext auf 90%+ bringen`,
      route: `/course/${courseId}/fill-in`,
      priority: 48,
    });
  }

  // Priority 6: All good - general maintenance
  if (recommendations.length === 0) {
    if (hasFlashcards) {
      recommendations.push({
        emoji: '✅',
        text: 'Alles gut! Wiederhole die Lernkarten zur Festigung.',
        route: `/course/${courseId}/flashcards`,
        priority: 30,
      });
    } else if (hasQuiz) {
      recommendations.push({
        emoji: '✅',
        text: 'Alles erledigt! Wiederhole das Quiz zur Sicherheit.',
        route: `/course/${courseId}/quiz`,
        priority: 30,
      });
    }
  }

  // Return highest priority recommendation
  recommendations.sort((a, b) => b.priority - a.priority);
  return recommendations[0] || {
    emoji: '🎉',
    text: 'Perfekt vorbereitet!',
    route: `/course/${courseId}`,
    priority: 0,
  };
}

// Format time ago
export function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'gerade eben';
  if (diffMins < 60) return `vor ${diffMins} Min.`;
  if (diffHours < 24) return `vor ${diffHours} Std.`;
  if (diffDays === 1) return 'gestern';
  if (diffDays < 7) return `vor ${diffDays} Tagen`;
  return date.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' });
}

// Format activity name for display
export function formatActivityName(action: string): string {
  const names: Record<string, string> = {
    'quiz': 'Quiz',
    'fill_in': 'Lückentext',
    'fill_in_quiz': 'Lückentext',
    'transform_quiz': 'Indirect Speech',
    'flashcards': 'Lernkarten',
    'vocab_match': 'Vokabel-Spiel',
    'lesson': 'Lektion',
    'lesson_complete': 'Lektion',
  };
  return names[action] || action;
}

// Format test date countdown
export function formatTestCountdown(testDate: string): { text: string; urgent: boolean } {
  const test = new Date(testDate);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  test.setHours(0, 0, 0, 0);

  const diffDays = Math.ceil((test.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return {
      text: `Test war am ${test.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' })}`,
      urgent: false,
    };
  }
  if (diffDays === 0) {
    return { text: 'Heute!', urgent: true };
  }
  if (diffDays === 1) {
    return { text: 'Morgen!', urgent: true };
  }
  if (diffDays <= 3) {
    return { text: `in ${diffDays} Tagen`, urgent: true };
  }
  if (diffDays <= 7) {
    return { text: `in ${diffDays} Tagen`, urgent: false };
  }
  return {
    text: `am ${test.toLocaleDateString('de-DE', { day: 'numeric', month: 'short' })}`,
    urgent: false,
  };
}
